import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2, Layers, Palette, Code2, Users, Headphones, ShieldCheck, Loader2, Landmark, Banknote, GraduationCap, Scale, Zap, FileSpreadsheet, BarChart3 } from "lucide-react";
import { toast } from "sonner";
import { SiteNav } from "@/components/SiteNav";
import { SiteFooter } from "@/pages/Landing";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { api, formatError } from "@/lib/api";

const FEATURES = [
  { icon: Layers, title: "Bulk sending", body: "Upload a CSV of recipients and send thousands of verified letters in one batch, with real-time progress." },
  { icon: Palette, title: "White-label", body: "Present every letter under your organisation's name, logo and colours across recipient emails, claim pages and delivery certificates, with a 'Verified by PostalCloud' mark so recipients know it's genuine. Recipients' replies can go to your organisation's own confirmed inbox." },
  { icon: Code2, title: "Enterprise API", body: "PostalCloud offers an enterprise API that allows approved organisations to send verified digital letters and retrieve proof-of-delivery records directly from their internal systems.", to: "/developers" },
  { icon: FileSpreadsheet, title: "Batch reports", body: "Enterprise users can export batch reports to track delivery status, claims, and pending recipients." },
  { icon: Users, title: "Team accounts", body: "Invite colleagues with their own logins and roles (Owner, Admin, Sender, Viewer), all working from one organisation's letters, batches and billing. Ownership can be handed over securely, every change is recorded in the audit trail, Owners are alerted to branding changes, and Owners and Admins can search and export their organisation's full audit history." },
  { icon: BarChart3, title: "Claim insights", body: "See claim rates over time, progress for every batch, and which recipients still need a reminder, with one-click follow-ups." },
  { icon: Headphones, title: "SLA & dedicated support", body: "SLA-backed uptime, a named account manager and priority onboarding." },
  { icon: ShieldCheck, title: "Court-ready proof", body: "Identity-matched claims, one-time codes, delivery certificates and a tamper-evident audit trail on every letter." },
];
const SECTORS = [
  { icon: Landmark, name: "Councils & government", note: "Council tax, housing, parking, social care, school admissions" },
  { icon: Banknote, name: "Banks & insurers", note: "Policies, statements, compliance notices, fraud alerts" },
  { icon: GraduationCap, name: "Universities", note: "Offers, exam results, finance and visa letters" },
  { icon: Scale, name: "Courts & legal", note: "Summons, judgements, case updates" },
  { icon: Zap, name: "Utilities", note: "Bills, disconnection notices, tariff updates" },
];
const COMPARE = [["UK 1st Class", "£1.80", "£1.20 cheaper"], ["UK 2nd Class", "£0.91", "£0.31 cheaper"], ["International", "£3.60", "£3.00 cheaper"]];
const TIERS = [
  { name: "Pay as you go", price: "£0.60", unit: "per letter · VAT incl.", points: ["No subscription", "Email + SMS notification", "Delivery certificate"], cta: "Start now", to: "/register" },
  { name: "Bulk volume", price: "From £0.45", unit: "per letter at 100k+ / month", points: ["CSV bulk upload", "API-triggered sends", "Volume discounts"], highlight: true },
  { name: "White-label", price: "£10k–£50k", unit: "per year", points: ["Your brand & domain", "Dedicated storage", "SLA & dedicated support"] },
];

const Hero = () => (
  <section className="bg-slate-950 text-white relative overflow-hidden grain" data-testid="enterprise-hero">
    <div className="relative max-w-7xl mx-auto px-6 py-24 lg:py-32">
      <p className="rise text-xs font-semibold uppercase tracking-[0.3em] text-emerald-400">PostalCloud Enterprise</p>
      <h1 className="rise font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[0.95] mt-5 max-w-4xl" style={{ animationDelay: "0.1s" }}>
        Digital postal infrastructure for organisations that send at scale.
      </h1>
      <p className="rise text-slate-300 text-base md:text-lg mt-6 max-w-2xl" style={{ animationDelay: "0.2s" }}>
        Replace printing, envelopes, franking and returned-mail handling with verified, identity-matched delivery and a tamper-evident audit trail, under your brand if you want it.
      </p>
      <div className="rise flex flex-wrap gap-3 mt-9" style={{ animationDelay: "0.3s" }}>
        <a href="#enquire" data-testid="enterprise-hero-cta" className="group inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-emerald-600 font-semibold hover:bg-emerald-500 transition-colors">Talk to sales <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" /></a>
        <a href="#pricing" className="inline-flex items-center px-6 py-3.5 rounded-full border border-white/30 font-semibold hover:bg-white hover:text-slate-900 transition-colors" data-testid="enterprise-pricing-link">See pricing</a>
      </div>
    </div>
  </section>
);

const Features = () => (
  <section className="max-w-7xl mx-auto px-6 py-24" data-testid="enterprise-features">
    <p className="text-xs font-semibold uppercase tracking-[0.2em] text-emerald-600">What's included</p>
    <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-3 max-w-2xl">Everything a high-volume sender needs.</h2>
    <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-slate-200 dark:bg-slate-800 border border-slate-200 dark:border-slate-800">
      {FEATURES.map(({ icon: Icon, title, body, to }) => (
        <div key={title} className="bg-white dark:bg-slate-950 p-8 group">
          <Icon size={22} className="text-emerald-600 group-hover:-translate-y-0.5 transition-transform" />
          <h3 className="font-display text-lg font-semibold mt-5">{title}</h3>
          <p className="text-sm text-slate-600 dark:text-slate-400 mt-2 leading-relaxed">{body}</p>
          {to && <Link to={to} data-testid="enterprise-api-docs-link" className="inline-flex items-center gap-1 mt-4 text-sm font-semibold text-emerald-600 hover:underline">Read the API docs <ArrowRight size={14} /></Link>}
        </div>
      ))}
    </div>
  </section>
);

const Compare = () => (
  <section className="border-y border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/40" data-testid="enterprise-compare">
    <div className="max-w-7xl mx-auto px-6 py-20 grid lg:grid-cols-12 gap-10 items-center">
      <div className="lg:col-span-5">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-emerald-600">Cost advantage</p>
        <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-3">Up to 83% cheaper than posting.</h2>
        <p className="text-sm text-slate-600 dark:text-slate-400 mt-4">67% cheaper than 1st Class, 33% cheaper than 2nd Class, and a flat £0.60 to anywhere in the world. Compared with Royal Mail 2026 letter prices (0–100g).</p>
      </div>
      <div className="lg:col-span-7 overflow-x-auto rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950">
        <table className="w-full text-sm" data-testid="enterprise-compare-table">
          <thead><tr className="text-left text-[11px] uppercase tracking-wider text-slate-500 border-b border-slate-200 dark:border-slate-800">
            {["Letter type", "Royal Mail", "PostalCloud", "Saving"].map((h) => <th key={h} className="px-5 py-3 font-semibold">{h}</th>)}
          </tr></thead>
          <tbody>{COMPARE.map(([t, rm, s]) => (
            <tr key={t} className="border-b last:border-0 border-slate-100 dark:border-slate-800">
              <td className="px-5 py-4 font-medium">{t}</td><td className="px-5 py-4 text-slate-500 line-through">{rm}</td>
              <td className="px-5 py-4 font-semibold">£0.60</td><td className="px-5 py-4 text-emerald-600 font-semibold">{s}</td>
            </tr>))}
          </tbody>
        </table>
      </div>
    </div>
  </section>
);

const Pricing = () => (
  <section id="pricing" className="max-w-7xl mx-auto px-6 py-24" data-testid="enterprise-pricing">
    <p className="text-xs font-semibold uppercase tracking-[0.2em] text-emerald-600">Pricing</p>
    <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-3">Pay per letter, or scale with volume.</h2>
    <div className="mt-12 grid md:grid-cols-3 gap-6">
      {TIERS.map((t) => (
        <div key={t.name} data-testid={`enterprise-tier-${t.name.toLowerCase().replace(/\s+/g, "-")}`} className={`rounded-2xl p-8 flex flex-col ${t.highlight ? "bg-slate-900 text-white" : "border border-slate-200 dark:border-slate-800"}`}>
          <p className="text-xs font-semibold uppercase tracking-wider text-emerald-500">{t.name}</p>
          <p className="font-display text-4xl font-extrabold mt-4">{t.price}</p>
          <p className={`text-sm mt-1 ${t.highlight ? "text-slate-400" : "text-slate-500"}`}>{t.unit}</p>
          <ul className="mt-6 space-y-2 text-sm flex-1">{t.points.map((p) => <li key={p} className="flex gap-2 items-center"><CheckCircle2 size={15} className="text-emerald-500 shrink-0" />{p}</li>)}</ul>
          {t.to
            ? <Link to={t.to} className="mt-8 inline-flex justify-center px-5 py-3 rounded-full border border-slate-300 dark:border-slate-700 font-semibold text-sm hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors">{t.cta}</Link>
            : <a href="#enquire" className="mt-8 inline-flex justify-center px-5 py-3 rounded-full bg-emerald-600 text-white font-semibold text-sm hover:bg-emerald-500 transition-colors">Request a quote</a>}
        </div>
      ))}
    </div>
    <p className="text-xs text-slate-500 mt-6">Volume and white-label pricing is confirmed in a tailored quote based on your monthly volume and requirements.</p>
  </section>
);

const Sectors = () => (
  <section className="max-w-7xl mx-auto px-6 pb-24" data-testid="enterprise-sectors">
    <h2 className="font-display text-2xl font-bold tracking-tight">Built for sectors that send millions of letters.</h2>
    <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
      {SECTORS.map(({ icon: Icon, name, note }) => (
        <div key={name} className="rounded-xl border border-slate-200 dark:border-slate-800 p-5 hover:border-emerald-500 transition-colors">
          <Icon size={20} className="text-emerald-600" /><p className="font-semibold mt-3 text-sm">{name}</p><p className="text-xs text-slate-500 mt-1">{note}</p>
        </div>
      ))}
    </div>
  </section>
);

const ORG_TYPES = ["Council / government", "Bank / insurer", "University / college", "Legal / courts", "Utility", "Care provider", "Other"];
const VOLUMES = ["Under 1,000", "1,000–10,000", "10,000–100,000", "100,000+"];
const INTERESTS = [["bulk", "Bulk sending (CSV)"], ["white_label", "White-label / custom branding"], ["api", "API integration"], ["sso", "Team accounts & SSO"], ["support", "Dedicated support & SLA"]];
const EMPTY = { org_name: "", contact_name: "", email: "", phone: "", org_type: "", monthly_volume: "", interests: [], message: "" };

const Chips = ({ options, value, onToggle, prefix, multi }) => (
  <div className="flex flex-wrap gap-2">
    {options.map(([v, l]) => {
      const on = multi ? value.includes(v) : value === v;
      return (
        <button key={v} type="button" data-testid={`${prefix}-${v.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} onClick={() => onToggle(v)}
          className={`px-3.5 py-2 rounded-full text-xs font-medium border transition-colors ${on ? "bg-emerald-600 text-white border-emerald-600" : "border-slate-300 dark:border-slate-700 hover:border-emerald-500"}`}>{l}</button>
      );
    })}
  </div>
);

const EnquiryForm = () => {
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const toggle = (v) => setF({ ...f, interests: f.interests.includes(v) ? f.interests.filter((i) => i !== v) : [...f.interests, v] });
  const submit = async (e) => {
    e.preventDefault();
    if (!f.org_type || !f.monthly_volume) return toast.error("Choose your organisation type and monthly volume");
    setBusy(true);
    try {
      await api.post("/enterprise/enquiries", f);
      setDone(true);
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setBusy(false);
    }
  };
  if (done) return (
    <div data-testid="enquiry-success" className="text-center py-10">
      <CheckCircle2 size={40} className="mx-auto text-emerald-600" />
      <p className="font-display text-xl font-semibold mt-4">Thanks, we've got your enquiry.</p>
      <p className="text-sm text-slate-500 mt-2">We'll be in touch within 2 working days. A confirmation is on its way to {f.email}.</p>
    </div>
  );
  return (
    <form onSubmit={submit} className="space-y-5" data-testid="enquiry-form">
      <div className="grid sm:grid-cols-2 gap-4">
        {[["org_name", "Organisation name"], ["contact_name", "Your name"], ["email", "Work email", "email"], ["phone", "Phone (optional)", "tel"]].map(([k, l, t]) => (
          <div key={k} className="space-y-2"><Label htmlFor={`e-${k}`}>{l}</Label>
            <Input id={`e-${k}`} data-testid={`enquiry-${k}-input`} type={t || "text"} required={k !== "phone"} value={f[k]} onChange={set(k)} className="h-11" /></div>
        ))}
      </div>
      <div className="space-y-2"><Label>Organisation type</Label><Chips prefix="enquiry-org" options={ORG_TYPES.map((o) => [o, o])} value={f.org_type} onToggle={(v) => setF({ ...f, org_type: v })} /></div>
      <div className="space-y-2"><Label>Letters per month</Label><Chips prefix="enquiry-volume" options={VOLUMES.map((o) => [o, o])} value={f.monthly_volume} onToggle={(v) => setF({ ...f, monthly_volume: v })} /></div>
      <div className="space-y-2"><Label>Interested in</Label><Chips prefix="enquiry-interest" multi options={INTERESTS} value={f.interests} onToggle={toggle} /></div>
      <div className="space-y-2"><Label htmlFor="e-message">Anything else? (optional)</Label>
        <Textarea id="e-message" data-testid="enquiry-message-input" rows={4} value={f.message} onChange={set("message")} placeholder="Tell us about the letters you send today" /></div>
      <button data-testid="enquiry-submit" type="submit" disabled={busy} className="w-full h-12 inline-flex items-center justify-center gap-2 rounded-full bg-slate-900 text-white dark:bg-white dark:text-slate-900 font-semibold hover:bg-emerald-600 transition-colors disabled:opacity-60">
        {busy && <Loader2 size={16} className="animate-spin" />} Request volume pricing
      </button>
    </form>
  );
};

const Enquire = () => (
  <section id="enquire" className="bg-slate-50 dark:bg-slate-900/40 border-t border-slate-200 dark:border-slate-800" data-testid="enterprise-enquire">
    <div className="max-w-7xl mx-auto px-6 py-24 grid lg:grid-cols-12 gap-10">
      <div className="lg:col-span-4">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-emerald-600">Talk to us</p>
        <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-3">Get a tailored quote.</h2>
        <p className="text-sm text-slate-600 dark:text-slate-400 mt-4">Tell us what you send and how much. We'll come back with volume pricing, a white-label option and an onboarding plan.</p>
        <p className="text-sm mt-6">Prefer email? <a href="mailto:bookings@postalcloud.app" className="underline hover:text-emerald-600" data-testid="enterprise-email-link">bookings@postalcloud.app</a></p>
      </div>
      <div className="lg:col-span-8 rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 p-6 sm:p-8"><EnquiryForm /></div>
    </div>
  </section>
);

export default function Enterprise() {
  return (
    <div data-testid="enterprise-page">
      <SiteNav />
      <Hero />
      <Features />
      <Compare />
      <Pricing />
      <Sectors />
      <Enquire />
      <SiteFooter />
    </div>
  );
}
